import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Flex, Icon, IconButton, Input, InputGroup, InputLeftElement,
  Select, Spinner, Table, Tbody, Td, Text, Th, Thead, Tr,
} from '@chakra-ui/react';
import { MdArrowDownward, MdArrowUpward, MdFileDownload, MdFilterList, MdSearch } from 'react-icons/md';
import api from '~/services/api';

type GuestAccess = {
  id: number;
  fullName: string;
  corporateEmail: string;
  whatsapp: string;
  cnpj: string;
  profile: string;
  productType: string;
  createdAt: string;
};

type ColumnKey = 'fullName' | 'corporateEmail' | 'whatsapp' | 'cnpj' | 'profile' | 'productType' | 'createdAt';
type ColumnFilters = {
  fullName: string;
  corporateEmail: string;
  whatsapp: string;
  cnpj: string;
  profile: string;
  productType: string;
  createdAt: string;
};

const columns: Array<{ key: ColumnKey; label: string }> = [
  { key: 'fullName', label: 'Nome' },
  { key: 'corporateEmail', label: 'E-mail' },
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'cnpj', label: 'CNPJ' },
  { key: 'profile', label: 'Perfil' },
  { key: 'productType', label: 'Produto' },
  { key: 'createdAt', label: 'Data de acesso' },
];

const emptyFilters: ColumnFilters = {
  fullName: '', corporateEmail: '', whatsapp: '', cnpj: '', profile: '', productType: '', createdAt: '',
};

const formatProduct = (value: string) => value === 'SEMI_HERMETICO' ? 'Semi-Hermético' : 'Hermético';
const formatDate = (value: string) => value ? new Date(value).toLocaleString('pt-BR') : '-';
const displayValue = (item: GuestAccess, key: ColumnKey) => {
  if (key === 'productType') return formatProduct(item.productType);
  if (key === 'createdAt') return formatDate(item.createdAt);
  return item[key];
};

const GuestUsers: React.FC = () => {
  const [items, setItems] = useState<GuestAccess[]>([]);
  const [search, setSearch] = useState('');
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>(emptyFilters);
  const [sortField, setSortField] = useState<ColumnKey>('createdAt');
  const [sortAscending, setSortAscending] = useState(false);
  const [pageSize, setPageSize] = useState(30);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get<GuestAccess[]>('/v1/guest/compressor-access')
      .then((response) => setItems(response.data))
      .catch((requestError) => {
        const status = requestError.response && requestError.response.status;
        setError(status ? `Não foi possível recuperar os usuários guest. Código: ${status}.` : 'Não foi possível recuperar os usuários guest.');
      })
      .finally(() => setLoading(false));
  }, []);

  const filteredItems = useMemo(() => {
    const globalTerm = search.trim().toLowerCase();
    const result = items.filter((item) => {
      const globalMatch = !globalTerm || columns.some((column) => String(displayValue(item, column.key)).toLowerCase().includes(globalTerm));
      const columnMatch = columns.every((column) => {
        const term = columnFilters[column.key].trim().toLowerCase();
        return !term || String(displayValue(item, column.key)).toLowerCase().includes(term);
      });
      return globalMatch && columnMatch;
    });

    return result.sort((left, right) => {
      const first = String(displayValue(left, sortField));
      const second = String(displayValue(right, sortField));
      const comparison = first.localeCompare(second, 'pt-BR', { numeric: true, sensitivity: 'base' });
      return sortAscending ? comparison : -comparison;
    });
  }, [columnFilters, items, search, sortAscending, sortField]);

  const updateColumnFilter = (field: ColumnKey, value: string) => {
    setColumnFilters((current) => ({ ...current, [field]: value }));
    setPage(1);
  };

  const changeSort = (field: ColumnKey) => {
    setPage(1);
    if (sortField === field) {
      setSortAscending((current) => !current);
    } else {
      setSortField(field);
      setSortAscending(true);
    }
  };

  const pageCount = Math.max(1, Math.ceil(filteredItems.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const pageItems = filteredItems.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const sortIcon = (field: ColumnKey) => {
    if (sortField !== field) return MdFilterList;
    return sortAscending ? MdArrowUpward : MdArrowDownward;
  };

  const exportExcel = () => {
    const headers = columns.map((column) => column.label);
    const rows = filteredItems.map((item) => columns.map((column) => displayValue(item, column.key)));
    const escape = (value: string) => String(value || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const html = `<table><tr>${headers.map((header) => `<th>${escape(header)}</th>`).join('')}</tr>${rows
      .map((row) => `<tr>${row.map((value) => `<td>${escape(value)}</td>`).join('')}</tr>`).join('')}</table>`;
    const blob = new Blob([`\ufeff${html}`], { type: 'application/vnd.ms-excel;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'usuarios-guest.xls';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box width="100%" px={{ base: 4, md: 8 }} py={6}>
      <Box border="1px solid #d9d9d9" borderRadius="20px" overflow="hidden" background="white" boxShadow="0 1px 5px rgba(0,0,0,.08)">
        <Flex px={{ base: 4, md: 7 }} py={4} align="center" justify="space-between" borderBottom="1px solid #e1e1e1" wrap="wrap" gap={4}>
          <Flex align="center" gap={3}>
            <Text fontSize="24px" color="#0b78c9" fontWeight="600" fontFamily="Arial, sans-serif">Usuários Guest</Text>
          </Flex>
          <Flex align="center" gap={3}>
            <Button leftIcon={<MdFileDownload />} onClick={exportExcel} colorScheme="blue" variant="outline" isDisabled={!filteredItems.length}>
              Extrair Excel
            </Button>
            <InputGroup width={{ base: '220px', md: '290px' }}>
              <InputLeftElement pointerEvents="none"><Icon as={MdSearch} color="gray.500" /></InputLeftElement>
              <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Pesquisar..." borderColor="gray.300" />
            </InputGroup>
          </Flex>
        </Flex>

        {loading && <Flex justify="center" py={10}><Spinner color="blue.500" /></Flex>}
        {error && <Text color="red.500" px={7} py={6}>{error}</Text>}
        {!loading && !error && (
          <Box overflowX="auto">
            <Table variant="simple" minW="1100px" fontFamily="Arial, sans-serif" fontSize="13px">
              <Thead>
                <Tr>
                  {columns.map((column) => (
                    <Th key={column.key} py={5} color="#10243e" fontSize="14px" textTransform="none" borderBottom="1px solid #d8d8d8">
                      <Flex align="center" gap={2} cursor="pointer" onClick={() => changeSort(column.key)}>
                        <Text>{column.label}</Text>
                        <Icon as={sortIcon(column.key)} color={sortField === column.key ? 'blue.500' : 'gray.500'} />
                      </Flex>
                    </Th>
                  ))}
                </Tr>
                <Tr background="#fcfcfc">
                  {columns.map((column) => (
                    <Th key={column.key} py={3} borderBottom="1px solid #d8d8d8">
                      <InputGroup size="sm">
                        <InputLeftElement pointerEvents="none"><Icon as={MdSearch} color="gray.500" /></InputLeftElement>
                        <Input value={columnFilters[column.key]} onChange={(event) => updateColumnFilter(column.key, event.target.value)} placeholder="Filtrar" border="0" borderBottom="1px solid #d8d8d8" borderRadius="0" />
                      </InputGroup>
                    </Th>
                  ))}
                </Tr>
              </Thead>
              <Tbody>
                {pageItems.map((item) => (
                  <Tr key={item.id} _hover={{ background: '#f5faff' }}>
                    {columns.map((column) => <Td key={column.key} py={4} color="#172b4d">{displayValue(item, column.key)}</Td>)}
                  </Tr>
                ))}
                {!filteredItems.length && <Tr><Td colSpan={columns.length}><Text py={8} textAlign="center">Nenhum usuário encontrado.</Text></Td></Tr>}
              </Tbody>
            </Table>
          </Box>
        )}

        {!loading && !error && <Flex px={{ base: 4, md: 7 }} py={4} justify="space-between" align="center" color="gray.600" fontSize="14px">
          <Flex align="center" gap={4}>
            <Text>{filteredItems.length} de {items.length} registros</Text>
            <Text>Resultados por página</Text>
            <Select value={pageSize} onChange={(event) => { setPageSize(Number(event.target.value)); setPage(1); }} width="80px" size="sm" background="white">
              <option value={10}>10</option>
              <option value={30}>30</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </Select>
          </Flex>
          <Flex align="center" gap={3}>
            <Button size="sm" variant="ghost" onClick={() => setPage((current) => Math.max(1, current - 1))} isDisabled={currentPage === 1}>Anterior</Button>
            <Text> Página {currentPage} de {pageCount} </Text>
            <Button size="sm" variant="ghost" onClick={() => setPage((current) => Math.min(pageCount, current + 1))} isDisabled={currentPage === pageCount}>Próxima</Button>
            <IconButton aria-label="Limpar filtros" icon={<MdFilterList />} variant="ghost" colorScheme="blue" onClick={() => { setSearch(''); setColumnFilters(emptyFilters); setPage(1); }} />
          </Flex>
        </Flex>}
      </Box>
    </Box>
  );
};

export default GuestUsers;
